/**
 * N3 · Competência 5 (Contagem e probabilidade) · **eixo cognitivo III** · H1.32.K01
 *
 *   "Acrescentar bolas até a chance de sair escura ser a pedida."
 *
 * O primeiro item probabilístico do banco com manipulação, e o primeiro de C5 no eixo
 * III — a competência mais magra da matriz. A habilidade H1.32 pede, ao pé da letra,
 * *modificar parâmetros de modo a tornar a ocorrência de um evento mais, menos ou
 * igualmente provável*, e é isso que o aluno faz: mexe na urna, não calcula a urna.
 *
 * A ARMADILHA, e o motivo de o item existir: acrescentar bolas escuras faz crescer o
 * numerador **e** o denominador. Quem pensa "quero 2 em 3, logo ponho escuras até
 * serem 2/3 das que existem hoje" erra, porque as que ele põe também entram no total.
 * Na conta isso é uma linha que se esquece; na urna, é o pote enchendo.
 *
 * A CHANCE NUNCA APARECE NA TELA. O seletor acrescenta bolas e o desenho muda, mas
 * nenhum número diz a probabilidade — senão bastaria mover o seletor até o número
 * bater. É a mesma regra da prancha que não inclina, em `render/gangorra.ts`.
 *
 * A fração pedida nunca é 1/2: em 1/2 a resposta é "iguale as cores", que é justamente
 * o erro mais comum, e o distrator viraria a chave.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { describeUrna, urna } from '../../render/urna.ts';
import {
  adjetivo,
  capitalizar,
  conta,
  de,
  nome,
  oS,
  osN,
  verbo,
  type AdjetivoId,
  type NomeDoGenero,
  type NomeId,
  type Numero,
} from '../../core/pt.ts';
import { NOMES } from '../../core/lexico.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  PROB_IGUALOU_AS_CORES,
  PROB_RAZAO_POR_FRACAO,
  PROB_SEM_DESCONTAR,
  PROB_TOTAL_DE_HOJE,
  PROB_TROCOU_A_COR,
} from '../../schema/errors.ts';
import type { ItemTemplate, NumericEntryInteraction } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

/** O seletor não passa daqui: acima disso a urna não cabe em cinco fileiras. */
const MAX_ACRESCENTAR = 12;

interface Contexto {
  readonly texto: string;
  /**
   * O RECIPIENTE, COMO O TEXTO O CHAMA. Não existia: a prosa do item dizia "a urna"
   * em dezesseis lugares enquanto o contexto sorteado falava de um saco de festa
   * junina, de uma caixa de gincana ou de um pote de loja. Quem lia a variante das
   * fichas recebia dicas sobre uma urna que o enunciado nunca mencionou.
   */
  readonly recipiente: NomeId;
  /**
   * O que há dentro: uma chave do léxico, e FEMININA. Várias frases do item são escritas
   * contando com isso — "das 9 que o saco tem", "a chance de ela ser", "quantas dessas" —,
   * e o tipo garante que uma peça masculina não compile.
   */
  readonly peca: NomeDoGenero<'f'>;
  /**
   * As duas marcas. Não são sempre cores — as bolinhas do pote são claras ou escuras —,
   * e o item chamava as duas de "cores" na explicação. (As fichas eram lisas ou
   * pintadas; o autor pediu brancas e pretas, em 2026-09-29.)
   */
  readonly clara: AdjetivoId;
  readonly escura: AdjetivoId;
  readonly sorteio: string;
}

const CONTEXTOS: readonly Contexto[] = [
  {
    texto:
      'Numa brincadeira de festa junina, um saco tem bolas brancas e bolas pretas, todas do mesmo tamanho. Quem tira uma bola preta ganha um prêmio.',
    recipiente: 'saco',
    peca: 'bola',
    clara: 'branco',
    escura: 'preto',
    sorteio: 'tirando uma bola do saco, sem olhar',
  },
  {
    texto:
      'Numa gincana, as fichas de um sorteio ficam numa caixa: umas de cor branca e outras de cor preta. Quem tira uma ficha preta escolhe a próxima prova.',
    recipiente: 'caixa',
    peca: 'ficha',
    clara: 'branco',
    escura: 'preto',
    sorteio: 'tirando uma ficha da caixa, sem olhar',
  },
  {
    texto:
      'Uma loja faz um sorteio com bolinhas iguais dentro de um pote: as claras não valem nada e as escuras valem desconto.',
    recipiente: 'pote',
    peca: 'bolinha',
    clara: 'claro',
    escura: 'escuro',
    sorteio: 'tirando uma bolinha do pote, sem olhar',
  },
];

/** As frações pedidas. 1/2 fica de fora: ali a resposta É o erro mais comum. */
const ALVOS: readonly (readonly [number, number])[] = [
  [2, 3],
  [3, 4],
  [3, 5],
];

export interface Params {
  readonly contexto: number;
  readonly claras: number;
  readonly escuras: number;
  /** A fração pedida, como numerador e denominador. */
  readonly alvo: readonly [number, number];
}

/** Quantas escuras faltam: a/b = (e+n)/(c+e+n)  ⇒  n = (a(c+e) − b·e) / (b−a). */
function resposta(p: Params): number {
  const [a, b] = p.alvo;
  return (a * (p.claras + p.escuras) - b * p.escuras) / (b - a);
}

const n0 = (x: number): string => formatBR(Math.round(x), 0);
const fracao = (p: Params): string => `${p.alvo[0]} em ${p.alvo[1]}`;

function drawParams(rng: Rng): Params {
  for (let tentativa = 0; tentativa < 80; tentativa++) {
    const claras = rng.int(3, 9);
    const escuras = rng.int(1, 5);
    const alvo = rng.pick(ALVOS) as readonly [number, number];
    // O total de HOJE é múltiplo do denominador. Duas razões: os erros com nome caem
    // todos em número inteiro de bolas — e, sem isso, três de cada quatro variantes eram
    // rejeitadas por falta de distrator ao alcance do seletor —; e a armadilha fica mais
    // tentadora, porque "2/3 das 9 que já estão aí" dá uma conta redonda e convincente.
    if ((claras + escuras) % alvo[1] !== 0) continue;
    const p: Params = { contexto: 0, claras, escuras, alvo };
    const n = resposta(p);
    // Inteira, alcançável pelo seletor, e nunca 1 ou 2: com uma bola só, "acrescente
    // até dar 2 em 3" se resolve olhando, e o item não mede nada.
    if (!Number.isInteger(n) || n < 3 || n > MAX_ACRESCENTAR) continue;
    if (claras + escuras + n > 25) continue;
    return { ...p, contexto: rng.int(0, CONTEXTOS.length - 1) };
  }
  return { contexto: rng.int(0, CONTEXTOS.length - 1), claras: 4, escuras: 1, alvo: [2, 3] };
}

interface Erro {
  readonly value: number;
  readonly error: ErrorCode;
  readonly explanation: RichText;
}

/**
 * Os erros com nome. Todos são maneiras de esquecer que as bolas acrescentadas também
 * entram no total — cada um esquecendo num lugar diferente da conta.
 */
function erros(p: Params): Erro[] {
  const [a, b] = p.alvo;
  const ctx = CONTEXTOS[p.contexto] as Contexto;
  const c = p.claras;
  const e = p.escuras;
  const certa = resposta(p);
  const total = c + e + certa;
  /** "12 bolas escuras", "1 ficha preta" — a contagem com a marca, concordadas. */
  const escuras = (n: number): string => String(conta(n0(n), ctx.peca).adj(ctx.escura));
  const claras = (n: number): string => String(conta(n0(n), ctx.peca).adj(ctx.clara));
  const oRecipiente = String(oS(ctx.recipiente));
  const contaTxt =
    `${a}/${b} de ${n0(total)} são ${escuras((a / b) * total)}, e ${oRecipiente} já tinha ${n0(e)}: ` +
    `faltam ${n0(certa)}`;

  const candidatos: Erro[] = [
    {
      value: c - e,
      error: PROB_IGUALOU_AS_CORES,
      explanation: [
        txt(`Com ${n0(c - e)} a mais, os dois tipos ficam com ${n0(c)} cada um — meio a meio. `),
        txt(`A chance pedida é ${fracao(p)}, que é mais da metade: ${contaTxt}.`),
      ],
    },
    {
      value: (a / b) * (c + e) - e,
      error: PROB_TOTAL_DE_HOJE,
      explanation: [
        txt(`Você tomou ${a}/${b} das ${n0(c + e)} que ${oRecipiente} tem HOJE. `),
        txt(
          `Mas ${osN(ctx.peca)} que entram também contam no total: depois de acrescentar, ${oRecipiente} tem `,
        ),
        txt(
          `${n0(total)}, e não ${n0(c + e)}. Fazendo a conta sobre o total de depois: ${contaTxt}.`,
        ),
      ],
    },
    {
      value: (a / b) * (c + e),
      error: PROB_SEM_DESCONTAR,
      explanation: [
        // Sem o "e não a de ACRESCENTAR" a frase seria só a conta certa repetida. E o
        // fecho não repete "já tinha", porque `contaTxt` termina exatamente nisso.
        txt(
          `Esse é o número de ${nome(ctx.peca, 'pl').adj(ctx.escura)} que o total precisa TER no fim, `,
        ),
        txt(`e não o número a ACRESCENTAR: ${contaTxt}.`),
      ],
    },
    {
      value: (a / b) * c - e,
      error: PROB_RAZAO_POR_FRACAO,
      explanation: [
        txt(`Isso deixaria ${escuras(a)} para cada ${claras(b)} — uma razão entre os dois tipos. `),
        txt(
          `"${fracao(p)}" é sobre o TOTAL de ${nome(ctx.peca, 'pl')}, e não sobre ${osN(ctx.peca).adj(ctx.clara)}: ${contaTxt}.`,
        ),
      ],
    },
    {
      value: c / (a / b) - c - e,
      error: PROB_TROCOU_A_COR,
      explanation: [
        txt(
          `Assim quem fica com ${fracao(p)} ${verbo('ser.pres', 'pl')} ${osN(ctx.peca).adj(ctx.clara)}, e a pergunta é sobre ${osN(ctx.peca).adj(ctx.escura)}. `,
        ),
        txt(`Trocando de volta: ${contaTxt}.`),
      ],
    },
  ];

  // Só o que o seletor alcança, em número inteiro, longe da resposta e sem repetir.
  const out: Erro[] = [];
  for (const x of candidatos) {
    if (!Number.isInteger(x.value)) continue;
    if (x.value < 0 || x.value > MAX_ACRESCENTAR) continue;
    if (x.value === certa) continue;
    if (out.some((o) => o.value === x.value)) continue;
    out.push(x);
  }
  return out;
}

export const urnaChance: ItemTemplate<Params, number> = {
  id: templateId('h1-32-K01-urna'),
  version: 1,
  taskModel: taskModelCode('H1.32.K01'),
  skills: [skillCode('H1.32')],
  axis: 'III',
  level: 'N3',
  title: 'Acrescentar bolas até a chance ser a pedida',
  responseModel: '2PL',
  difficultySources: ['steps.2', 'inverse-problem'],
  params: drawParams,
  solve: resposta,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Denominador 5 pede uma divisão que não sai de cabeça.
    if (p.alvo[1] === 5) fontes.push('non-round-numbers');
    if (p.escuras >= 4) fontes.push('steps.3plus');
    return fontes;
  },
  validate(_b, p) {
    const n = resposta(p);
    if (!Number.isInteger(n)) return 'a resposta não é um número inteiro de bolas';
    if (n < 3 || n > MAX_ACRESCENTAR) return 'a resposta não cabe no seletor';
    // Dois erros com nome bastam. Num item de seletor não há cinco alternativas: o erro
    // só é nomeado quando o aluno para exatamente nele, e exigir três rejeitava duas em
    // cada cinco variantes boas.
    if (erros(p).length < 2) return 'menos de dois erros com nome ao alcance do seletor';
    return null;
  },
  build(p, certa) {
    const c = CONTEXTOS[p.contexto] as Contexto;
    const [a, b] = p.alvo;
    const total = p.claras + p.escuras + certa;
    const alt = describeUrna(c, p.claras, p.escuras);
    /** A marca escura concordada com a peça — "preta", "escuras" —, sem a peça na frente. */
    const escura = (n: Numero): string => adjetivo(c.escura, NOMES[c.peca].g, n);

    const interaction: NumericEntryInteraction = {
      kind: 'numeric-entry',
      decimals: 0,
      keyValue: certa,
      // O seletor anda de um em um; meia bola não existe.
      absTol: 0.4,
      errorValues: erros(p),
      viaControl: { stimulusId: 'urna', controlId: 'acrescentar' },
    };

    return {
      stem: [
        txt(c.texto),
        br,
        txt('Hoje há '),
        strong(String(conta(n0(p.claras), c.peca).adj(c.clara))),
        txt(' e '),
        strong(String(conta(n0(p.escuras), c.peca).adj(c.escura))),
        txt('.'),
      ],
      comando: [
        txt(
          `Acrescente ${nome(c.peca, 'pl').adj(c.escura)} até que, ${c.sorteio}, a chance de ela ser ${escura('sg')} seja de `,
        ),
        strong(fracao(p)),
        txt('.'),
      ],
      stimuli: [
        {
          kind: 'manipulable',
          motion: 'interactive',
          audio: { need: 'none' },
          id: 'urna',
          alt,
          controls: [
            {
              id: 'acrescentar',
              label: [
                txt(`${capitalizar(String(nome(c.peca, 'pl').adj(c.escura)))} a acrescentar`),
              ],
              min: 0,
              max: MAX_ACRESCENTAR,
              step: 1,
              initial: 0,
              format: (v) => n0(v),
            },
          ],
          frame: (v) => {
            const acrescentadas = v['acrescentar'] ?? 0;
            return urna({
              claras: p.claras,
              escuras: p.escuras,
              acrescentadas,
              reserva: p.claras + p.escuras + MAX_ACRESCENTAR,
              title: `${capitalizar(String(oS(c.recipiente)))} do sorteio`,
              desc: describeUrna(c, p.claras, p.escuras, acrescentadas),
            });
          },
          quadroImpresso: { acrescentar: 0 },
        },
      ],
      interaction,
      dicas: [
        [
          txt('Chance de '),
          strong(fracao(p)),
          txt(
            ` quer dizer: de cada ${conta(b, c.peca)} ${de(oS(c.recipiente))}, ${a} são ${escura('pl')}. É uma fração do `,
          ),
          strong('total'),
          txt(`, e não ${de(osN(c.peca).adj(c.clara))}.`),
        ],
        [
          txt('Cuidado com o total: '),
          strong(String(osN(c.peca).adj(c.escura))),
          txt(
            ` que você acrescenta também entram na contagem do total. ${capitalizar(String(oS(c.recipiente)))} fica maior do que era.`,
          ),
        ],
        [
          txt(
            `Se o total no fim for ${n0(total)}, quantas precisam ser ${escura('pl')}? E quantas dessas ${oS(c.recipiente)} já tinha?`,
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `Chame de n a quantidade de ${nome(c.peca, 'pl').adj(c.escura)} acrescentadas. No fim, ${oS(c.recipiente)} terá `,
        ),
        txt(
          `${n0(p.claras)} + ${n0(p.escuras)} + n ${nome(c.peca, 'pl')}, das quais ${n0(p.escuras)} + n ${escura('pl')}.`,
        ),
        br,
        txt(
          `A chance pedida é ${a}/${b}, então (${n0(p.escuras)} + n) deve ser ${a}/${b} de (${n0(p.claras + p.escuras)} + n).`,
        ),
        br,
        txt(
          `Testando o total ${n0(total)}: ${a}/${b} de ${n0(total)} são ${conta(n0((a / b) * total), c.peca).adj(c.escura)}. `,
        ),
        txt(
          `Como ${oS(c.recipiente)} já tinha ${n0(p.escuras)}, faltam ${n0((a / b) * total)} − ${n0(p.escuras)} = `,
        ),
        strong(`${n0(certa)}`),
        txt('.'),
        br,
        txt(`Conferindo: ${n0((a / b) * total)} de ${n0(total)} é ${fracao(p)}. `),
        txt('Repare que o total cresceu junto: era '),
        txt(`${n0(p.claras + p.escuras)} e passou a ${n0(total)}.`),
      ],
      declared: 'media',
    };
  },
};
